import React, { useEffect, useState } from "react";
import { getPlansData, triggerAction } from "frontend/api";
import { CheckoutSource } from "frontend/billing/checkout";
import PaymentModal from "frontend/billing/payment-modal";
import PaymentPage from "frontend/billing/payment-page";
import tracking from "frontend/tracking";
import { getPriceByPlanMapping } from "frontend/utils/billing-utils";
import consts from "shared/consts";
import { User } from "shared/datamodel/schemas/user";
import { plansDataAtom } from "state-atoms/billing-atoms";
import useSeatsCount from "frontend/hooks/use-seats-count";
import { PlanData } from "shared/datamodel/schemas";
import { useFetchWithJotai } from "frontend/hooks/use-fetch-with-jotai";
import AppLoader from "frontend/loader/app-loader";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { onUpgradeClickedOnMondayApp } from "utils/monday-app-utils";
import { isCIM } from "utils/url-utils";

function noModalToRender() {
  tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "no-plansData-value");
  return null;
}

export default function UpgradeTrialModal({
  user,
  onDismiss,
  source,
  renderType,
}: {
  user: User;
  onDismiss: () => void;
  source?: CheckoutSource;
  renderType: "modal" | "page";
}) {
  //atoms
  const { data: plansData, isLoading } = useFetchWithJotai<PlanData[]>({
    key: "plansData",
    fetchFn: getPlansData,
    atom: plansDataAtom,
  });
  const [seatsCount, setSeatsCount] = useState<number>(user.planInfo?.seats_count ?? 0);

  const { userAccountCount, loadUserAccountCount } = useSeatsCount(user);
  const isPerSeatLicenseEnabled = useFeatureFlag("per-seat-license");
  const showTrial = !user?.planInfo?.is_downgraded;

  useEffect(() => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "trial-choose-plan-modal-showed", renderType);
    (async () => {
      try {
        await triggerAction(consts.MAIL_TRIGGERS.UPGRADE_PLAN);
      } catch {
        // couldn't trigger
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      user && (await loadUserAccountCount());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPerSeatLicenseEnabled]);

  useEffect(() => {
    setSeatsCount(Math.max(userAccountCount, seatsCount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccountCount]);

  function renderPaymentModal(plansData: PlanData[]) {
    const selectedPlanData = plansData.find((plan) => plan.name.toLowerCase() === "pro"); //We assume all plans have the same trial days here, so we just take randomally from the teams plan. We should change it if this assumption changes
    const trialDays = selectedPlanData?.trialDays ?? consts.TRIAL_DAYS_DEFAULT_PERIOD;
    const priceByPlan = getPriceByPlanMapping(plansData);

    return (
      <PaymentModal
        showTrial={showTrial}
        priceByPlan={priceByPlan}
        trialDays={trialDays}
        seatsCount={seatsCount}
        onDismiss={onDismiss}
        source={source}
      />
    );
  }

  function renderPaymentPage(plansData: PlanData[]) {
    const selectedPlanData = plansData.find((plan) => plan.name.toLowerCase() === "pro"); //We assume all plans have the same trial days here, so we just take randomally from the teams plan. We should change it if this assumption changes
    const trialDays = selectedPlanData?.trialDays ?? consts.TRIAL_DAYS_DEFAULT_PERIOD;
    const priceByPlan = getPriceByPlanMapping(plansData);

    return (
      <PaymentPage
        showTrial={showTrial}
        priceByPlan={priceByPlan}
        trialDays={trialDays}
        seatsCount={seatsCount}
        onDismiss={onDismiss}
        source={source}
      />
    );
  }

  function render() {
    if (isCIM()) {
      onUpgradeClickedOnMondayApp(source?.value || "");
      onDismiss();
      return null;
    } else if (isLoading) {
      return <AppLoader />;
    } else if (plansData) {
      return renderType === "page" ? renderPaymentPage(plansData) : renderPaymentModal(plansData);
    } else {
      return noModalToRender();
    }
  }

  return render();
}
